import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  ChevronDown, ChevronRight, Radar, Loader2, DollarSign,
  TrendingUp, TrendingDown, FlaskConical, Filter,
} from "lucide-react";
import { Ticker } from "@/components/TickerDrawer";

const SOURCES = [
  { id: "ALPACA_PAPER",       label: "Alpaca Paper" },
  { id: "INTERNAL_PAPER_MOCK", label: "Internal Paper" },
  { id: "ALL",                  label: "All Sources" },
];

const DATE_RANGES = [
  { id: "today",     label: "Today"     },
  { id: "yesterday", label: "Yesterday" },
  { id: "7d",         label: "7D"         },
  { id: "30d",       label: "30D"       },
];

function _iso(d) {
  // Format a Date as YYYY-MM-DD in the ET trading-day timezone so
  // "Today" doesn't flip at UTC midnight (8 PM ET) — trading days
  // are anchored to America/New_York, not UTC.
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(d);
  const y = parts.find((p) => p.type === "year").value;
  const m = parts.find((p) => p.type === "month").value;
  const day = parts.find((p) => p.type === "day").value;
  return `${y}-${m}-${day}`;
}
function _rangeFor(id) {
  const now = new Date();
  const today = _iso(now);
  if (id === "today")     return { start_date: today, end_date: today, label: "Today" };
  const y = new Date(now); y.setDate(y.getDate() - 1);
  if (id === "yesterday") return { start_date: _iso(y), end_date: _iso(y), label: "Yesterday" };
  const past = new Date(now); past.setDate(past.getDate() - (id === "7d" ? 6 : 29));
  return { start_date: _iso(past), end_date: today, label: id.toUpperCase() };
}

function StatCell({ label, value, tone = "" }) {
  return (
    <div className="flex flex-col gap-0.5 min-w-[110px]">
      <div className="label-cap text-[9px]">{label}</div>
      <div className={`mono text-lg font-medium ${tone}`}>{value}</div>
    </div>
  );
}

function EventPill({ e }) {
  const t = e.event_type || "";
  const isFill = t.startsWith("FILL_");
  const isLedger = t.startsWith("LEDGER_");
  const isPos = t.startsWith("POSITION_");
  const cls = isFill
    ? "text-signal-green border-signal-green/40"
    : isPos
      ? "text-[#0A84FF] border-[#0A84FF]/40"
      : isLedger
        ? "text-signal-yellow border-signal-yellow/40"
        : "text-muted-foreground border-border/60";
  return (
    <span className={`chip !text-[9px] ${cls}`}>{t.replace(/_/g, " ")}</span>
  );
}

function TradeCard({ lc }) {
  const [open, setOpen] = useState(false);
  const agg = lc.aggregates || {};
  const pnl = agg.realized_pnl || 0;
  const isHarness = !!lc.harness;
  const status = lc.status || "OPEN";
  const dir = lc.direction || "";
  const first = lc.timeline?.[0]?.when || "";
  const last = lc.timeline?.[lc.timeline.length - 1]?.when || "";
  const tStart = first ? new Date(first).toLocaleTimeString() : "";
  const tEnd = last ? new Date(last).toLocaleTimeString() : "";
  return (
    <div className={`grid-cell !p-0 ${isHarness ? "opacity-90" : ""}`}
        data-testid={`activity-trade-${lc.symbol || "unknown"}`}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full px-3 py-2 flex items-center gap-2 text-left hover:bg-secondary/25 transition-colors"
        data-testid={`activity-trade-toggle-${lc.symbol || "unknown"}`}
      >
        {open ? <ChevronDown size={13}/> : <ChevronRight size={13}/>}
        <div className="mono text-base font-medium min-w-[80px]">
          <Ticker symbol={lc.symbol || "—"} size="sm"/>
        </div>
        {isHarness && (
          <span className="chip !text-[9px] text-signal-yellow border-signal-yellow/40 flex items-center gap-1">
            <FlaskConical size={9}/> SUPERVISED TEST
          </span>
        )}
        <span className={`chip !text-[9px] ${status === "CLOSED" ? "text-muted-foreground border-border/60" : "text-signal-green border-signal-green/40"}`}>
          {status}
        </span>
        {dir && (
          <span className="chip !text-[9px] text-muted-foreground border-border/60">
            {dir.replace(/_/g, " ")}
          </span>
        )}
        <div className="ml-auto flex items-center gap-4 text-[11px] mono text-muted-foreground">
          {agg.avg_entry_price != null && (
            <span>Entry ${Number(agg.avg_entry_price).toFixed(4)}</span>
          )}
          {agg.avg_exit_price != null && (
            <span>Exit ${Number(agg.avg_exit_price).toFixed(4)}</span>
          )}
          <span className={pnl > 0 ? "pnl-up" : pnl < 0 ? "pnl-down" : ""}>
            P&amp;L {pnl >= 0 ? "+" : ""}${pnl.toFixed(2)}
          </span>
          <span className="opacity-60">{tStart}{tEnd && tStart !== tEnd ? ` → ${tEnd}` : ""}</span>
        </div>
      </button>
      {open && (
        <div className="border-t border-border/60 p-3 space-y-2 bg-background/40">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[11px] mono">
            <StatCell label="ENTRY QTY" value={(agg.entry_qty || 0).toFixed(4)}/>
            <StatCell label="EXIT QTY" value={(agg.exit_qty || 0).toFixed(4)}/>
            <StatCell label="FEES" value={`$${(agg.fees || 0).toFixed(2)}`}/>
            <StatCell label="SLIPPAGE" value={`$${(agg.slippage || 0).toFixed(4)}`}/>
          </div>
          {lc.proposal_id && (
            <div className="text-[10px] mono text-muted-foreground truncate">
              proposal_id: <span className="text-foreground/80">{lc.proposal_id}</span>
              {lc.harness_run_id && (
                <span className="ml-3">harness_run: <span className="text-foreground/80">{lc.harness_run_id}</span></span>
              )}
              {lc.portfolio_key && (
                <span className="ml-3">portfolio: <span className="text-foreground/80">{lc.portfolio_key}</span></span>
              )}
            </div>
          )}
          <div>
            <div className="label-cap mb-1">Timeline ({lc.timeline?.length || 0} events)</div>
            <div className="border border-border/40">
              <table className="w-full text-[11px] mono">
                <tbody>
                  {(lc.timeline || []).map((e, i) => (
                    <tr key={i} className="border-b border-border/30 last:border-0">
                      <td className="px-2 py-1 text-muted-foreground whitespace-nowrap w-[110px]">
                        {e.when ? new Date(e.when).toLocaleTimeString() : "—"}
                      </td>
                      <td className="px-2 py-1"><EventPill e={e}/></td>
                      <td className="px-2 py-1 text-muted-foreground">
                        {e.qty != null && `qty=${Number(e.qty).toFixed(4)}`}
                        {e.price != null && ` @ $${Number(e.price).toFixed(4)}`}
                        {e.amount_cash != null && ` cash=$${Number(e.amount_cash).toFixed(2)}`}
                      </td>
                      <td className="px-2 py-1 text-[10px] opacity-60">{e.source_collection}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function TradingActivity() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [range, setRange] = useState("today");
  const [source, setSource] = useState(() =>
    (localStorage.getItem("tm_execution_source") || "ALPACA_PAPER").toUpperCase());
  const [includeHarness, setIncludeHarness] = useState(true);
  const [symbolFilter, setSymbolFilter] = useState("");

  const rng = useMemo(() => _rangeFor(range), [range]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {
        start_date: rng.start_date, end_date: rng.end_date,
        execution_source: source,
        include_harness: includeHarness,
      };
      if (symbolFilter.trim()) params.symbol = symbolFilter.trim().toUpperCase();
      const r = await api.get("/trading-activity", { params });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load activity");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [range, source, includeHarness, symbolFilter]);

  const s = data?.summary || {};
  const trades = data?.trades || [];
  const realTrades = trades.filter((t) => !t.harness);
  const harnessTrades = trades.filter((t) => t.harness);

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="trading-activity-root">
      <div className="flex items-center gap-2">
        <Radar size={16}/>
        <h1 className="text-xl font-medium tracking-tight">Trading Activity</h1>
        <div className="ml-auto text-[10px] mono uppercase tracking-widest text-muted-foreground">
          {rng.label} · {source.replace(/_/g, " ")}
        </div>
      </div>

      {/* Date range chips */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="label-cap">Range</span>
        {DATE_RANGES.map((r) => (
          <button
            key={r.id}
            onClick={() => setRange(r.id)}
            data-testid={`activity-range-${r.id}`}
            className={`chip ${range === r.id ? "!text-foreground !border-foreground" : ""}`}
          >{r.label}</button>
        ))}
        <span className="mx-3 opacity-30">·</span>
        <span className="label-cap">Source</span>
        {SOURCES.map((sr) => (
          <button
            key={sr.id}
            onClick={() => setSource(sr.id)}
            data-testid={`activity-source-${sr.id}`}
            className={`chip ${source === sr.id ? "!text-foreground !border-foreground" : ""}`}
          >{sr.label}</button>
        ))}
        <span className="mx-3 opacity-30">·</span>
        <button
          onClick={() => setIncludeHarness((v) => !v)}
          data-testid="activity-toggle-harness"
          className={`chip ${includeHarness ? "!text-foreground !border-foreground" : ""}`}
        >
          <FlaskConical size={10} className="inline mr-1"/>
          {includeHarness ? "Harness ON" : "Harness OFF"}
        </button>
        <input
          value={symbolFilter}
          onChange={(e) => setSymbolFilter(e.target.value)}
          placeholder="Symbol filter…"
          data-testid="activity-symbol-filter"
          className="ml-2 h-7 !text-[11px] mono uppercase bg-background border border-border/60 px-2 w-[130px]"
        />
      </div>

      {/* Daily Summary */}
      <section className="grid-cell !p-3">
        <div className="flex items-center gap-2 mb-2">
          <span className="label-cap">Summary</span>
          {loading && <Loader2 size={12} className="animate-spin"/>}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-sm">
          <StatCell label="TRADES" value={s.trades_today ?? "—"}/>
          <StatCell label="BUYS" value={s.buys ?? "—"}/>
          <StatCell label="SELLS" value={s.sells ?? "—"}/>
          <StatCell label="CLOSED" value={s.closed_trades ?? "—"}/>
          <StatCell
            label="REALIZED P&L"
            value={s.realized_pnl != null ? `${s.realized_pnl >= 0 ? "+" : ""}$${s.realized_pnl.toFixed(2)}` : "—"}
            tone={s.realized_pnl > 0 ? "pnl-up" : s.realized_pnl < 0 ? "pnl-down" : ""}
          />
          <StatCell label="FEES" value={s.fees != null ? `$${s.fees.toFixed(2)}` : "—"}/>
          <StatCell label="WINS" value={s.winning_trades ?? "—"} tone="pnl-up"/>
          <StatCell label="LOSSES" value={s.losing_trades ?? "—"} tone="pnl-down"/>
          <StatCell label="OPEN POS" value={s.open_positions ?? "—"}/>
          <StatCell label="OPEN ORDERS" value={s.open_orders ?? "—"}/>
          <StatCell label="TEST TRADES" value={s.harness_trades ?? "—"} tone="text-signal-yellow"/>
          <StatCell label="TEST CLOSED" value={s.harness_round_trips ?? "—"} tone="text-signal-yellow"/>
        </div>
        {!loading && realTrades.length === 0 && harnessTrades.length === 0 && (
          <div className="mt-3 text-xs text-muted-foreground">
            No trades in the selected window.
          </div>
        )}
        {!loading && realTrades.length === 0 && harnessTrades.length > 0 && (
          <div className="mt-3 text-xs text-signal-yellow" data-testid="activity-harness-only-note">
            No production trades in this window · {harnessTrades.length} supervised test round trip{harnessTrades.length === 1 ? "" : "s"} shown below.
          </div>
        )}
      </section>

      {/* Real trades */}
      {realTrades.length > 0 && (
        <section>
          <div className="label-cap mb-2 flex items-center gap-2">
            <TrendingUp size={12}/> Production Trades ({realTrades.length})
          </div>
          <div className="space-y-2">
            {realTrades.map((lc) => (
              <TradeCard key={lc.group_key} lc={lc}/>
            ))}
          </div>
        </section>
      )}

      {/* Harness trades — visually separated */}
      {harnessTrades.length > 0 && (
        <section>
          <div className="label-cap mb-2 flex items-center gap-2 text-signal-yellow">
            <FlaskConical size={12}/> Supervised Test / Harness ({harnessTrades.length})
            <span className="opacity-60 text-[10px] normal-case tracking-normal ml-2">
              Excluded from AI-performance metrics by default
            </span>
          </div>
          <div className="space-y-2">
            {harnessTrades.map((lc) => (
              <TradeCard key={lc.group_key} lc={lc}/>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
